import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiClock, FiX, FiZap } from 'react-icons/fi';
import { getMinutesBetween } from '../utils/dateUtils';

const EMPTY_FORM = {
  startTime: '19:00',
  endTime: '20:00',
  subject: '',
  planContent: '',
  actualContent: '',
  actualMinutes: 0,
  understanding: 0,
  status: 'planned',
  memo: '',
};

const ScheduleFormModal = ({
  initialPlan,
  isGeneratingAi,
  onAiRecommend,
  onClose,
  onSave,
  plans = [],
  selectedDate,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [entryMode, setEntryMode] = useState('manual');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const memoRef = useRef(null);

  useEffect(() => {
    setForm({ ...EMPTY_FORM, studyDate: selectedDate, ...initialPlan });
    setEntryMode(initialPlan?.isAiDraft ? 'ai' : 'manual');
    setError('');
  }, [initialPlan, selectedDate]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    const memoElement = memoRef.current;
    if (!memoElement) return;
    memoElement.style.height = 'auto';
    const borderHeight = memoElement.offsetHeight - memoElement.clientHeight;
    memoElement.style.height = `${memoElement.scrollHeight + borderHeight}px`;
  }, [form.memo]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleAiRecommend = async () => {
    if (!onAiRecommend || isGeneratingAi) return;
    setError('');
    try {
      const draft = await onAiRecommend();
      if (draft) setForm({ ...EMPTY_FORM, studyDate: selectedDate, ...draft });
    } catch (aiError) {
      setError(aiError.message || '추천 계획을 불러오지 못했습니다.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.subject.trim() || !form.planContent.trim()) {
      setError('과목과 계획 내용을 입력해 주세요.');
      return;
    }

    const expectedMinutes = getMinutesBetween(form.startTime, form.endTime);
    if (expectedMinutes <= 0) {
      setError('종료 시간은 시작 시간보다 늦어야 합니다.');
      return;
    }
    const conflictingPlan = plans.find((plan) => (
      Number(plan.sno) !== Number(form.sno)
      && form.startTime < plan.endTime
      && form.endTime > plan.startTime
    ));
    if (conflictingPlan) {
      setError(`${conflictingPlan.startTime}~${conflictingPlan.endTime} ${conflictingPlan.subject} 계획과 시간이 겹칩니다.`);
      return;
    }
    if (
      form.status === 'completed'
      && (!form.actualContent.trim() || Number(form.actualMinutes) <= 0 || Number(form.understanding) <= 0)
    ) {
      setError('완료하려면 실제 학습 내용, 시간, 이해도를 모두 기록해 주세요.');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        subject: form.subject.trim(),
        planContent: form.planContent.trim(),
        actualContent: form.actualContent.trim(),
        expectedMinutes,
        actualMinutes: Number(form.actualMinutes) || 0,
        understanding: Number(form.understanding) || 0,
        memo: form.memo.trim(),
      });
      onClose();
    } catch (saveError) {
      setError(saveError.message || '계획을 저장하지 못했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="scheduler-form-modal" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className="scheduler-form-modal__content"
        onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      >
        <form className="scheduler-form" onSubmit={handleSubmit}>
          <div className="scheduler-form__scroll-area">
            <header>
              <span>{entryMode === 'ai' ? <FiZap aria-hidden="true" /> : <FiClock aria-hidden="true" />}</span>
              <div>
                <h2>{form.sno ? '학습 계획 수정' : '학습 계획 추가'}</h2>
                <p>{selectedDate}</p>
              </div>
              <button type="button" onClick={onClose} aria-label="닫기"><FiX aria-hidden="true" /></button>
            </header>

            {!form.sno && onAiRecommend && (
              <div className="scheduler-form__mode" role="tablist" aria-label="학습 계획 입력 방법">
                <button
                  type="button"
                  role="tab"
                  aria-selected={entryMode === 'manual'}
                  className={entryMode === 'manual' ? 'is-selected' : ''}
                  onClick={() => setEntryMode('manual')}
                >
                  직접 입력
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={entryMode === 'ai'}
                  className={entryMode === 'ai' ? 'is-selected' : ''}
                  onClick={() => setEntryMode('ai')}
                >
                  AI 추천
                </button>
              </div>
            )}

            {!form.sno && entryMode === 'ai' && onAiRecommend && (
              <section className="scheduler-form__ai-panel">
                <div>
                  <FiZap aria-hidden="true" />
                  <span>
                    <strong>{form.isAiDraft ? 'AI 추천을 불러왔습니다.' : 'AI가 계획을 추천해요.'}</strong>
                    <small>{form.aiNotice || '기존 일정과 겹치지 않게 추천합니다.'}</small>
                  </span>
                </div>
                <button type="button" onClick={handleAiRecommend} disabled={isGeneratingAi}>
                  {isGeneratingAi
                    ? 'AI 추천을 만드는 중...'
                    : form.isAiDraft
                      ? 'AI 추천 다시 받기'
                      : 'AI 추천으로 채우기'}
                </button>
              </section>
            )}

            <div className="scheduler-form__time-row">
              <label>
                <span>시작 시간</span>
                <input type="time" value={form.startTime} onChange={(event) => updateField('startTime', event.target.value)} required />
              </label>
              <label>
                <span>종료 시간</span>
                <input type="time" value={form.endTime} onChange={(event) => updateField('endTime', event.target.value)} required />
              </label>
            </div>

            <label>
              <span>과목</span>
              <input
                type="text"
                list="scheduler-subject-list"
                value={form.subject}
                onChange={(event) => updateField('subject', event.target.value)}
                placeholder="예: 수학"
                maxLength={100}
                required
              />
              <datalist id="scheduler-subject-list">
                <option value="국어" /><option value="영어" /><option value="수학" />
                <option value="과학" /><option value="사회" /><option value="복습" /><option value="과제" />
              </datalist>
            </label>

            <label>
              <span>학습 내용</span>
              <textarea
                value={form.planContent}
                onChange={(event) => updateField('planContent', event.target.value)}
                placeholder="공부할 내용을 입력하세요."
                maxLength={1000}
                required
              />
            </label>

            <fieldset>
              <legend>진행 상태</legend>
              <div className="scheduler-form__status-options">
                {[['planned', '미완료'], ['progress', '진행 중'], ['completed', '완료']].map(([value, label]) => (
                  <button
                    type="button"
                    className={form.status === value ? 'is-selected' : ''}
                    onClick={() => updateField('status', value)}
                    key={value}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>

            <label>
              <span>실제 학습 내용 <small>선택</small></span>
              <textarea
                value={form.actualContent}
                onChange={(event) => updateField('actualContent', event.target.value)}
                placeholder="실제로 학습한 내용을 기록하세요."
                maxLength={1000}
              />
            </label>

            <div className="scheduler-form__record-row">
              <label>
                <span>실제 학습 시간(분)</span>
                <input type="number" min="0" max="1440" value={form.actualMinutes} onChange={(event) => updateField('actualMinutes', event.target.value)} />
              </label>
              <fieldset>
                <legend>이해도</legend>
                <div className="scheduler-form__rating">
                  {[1, 2, 3, 4, 5].map((score) => (
                    <button
                      type="button"
                      className={Number(form.understanding) >= score ? 'is-selected' : ''}
                      onClick={() => updateField('understanding', score)}
                      aria-label={`이해도 ${score}점`}
                      key={score}
                    >★</button>
                  ))}
                </div>
              </fieldset>
            </div>

            <label>
              <span>메모 <small>선택</small></span>
              <textarea
                className="scheduler-form__memo"
                ref={memoRef}
                value={form.memo}
                onChange={(event) => updateField('memo', event.target.value)}
                maxLength={1000}
                rows={3}
              />
            </label>

            {error && <p className="scheduler-form__error" role="alert">{error}</p>}
          </div>
          <button type="submit" className="scheduler-form__save" disabled={isSaving}>
            {isSaving ? '저장 중...' : '저장하기'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ScheduleFormModal;
